import { Component } from '@angular/core';
import {
  SituAnalyticsWebApiClient,
  ListRecipeEventSummariesRequest,
  ListRecipeEventSummariesResponse,
  RecipeEventSummary,
  RecipeStatus,
  ProductName,
  RendererType
} from 'situanalytics-webapi-client';
import { ChartOptions, ChartDataSets } from 'chart.js';
import { Label } from 'ng2-charts';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import { AuthService, BreadcrumbService, ProductNameService, StateService, CompanyService, IProductNameConfiguration } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as Chart from 'chart.js';

@Component({
  selector: 'render-summary-report',
  templateUrl: './render-summary-report.component.html',
  styleUrls: ['./render-summary-report.component.scss'],
})
export class RenderSummaryReportComponent extends SituBaseComponent {
  // Query param names
  readonly MonthParamName: string = 'month';
  readonly CompanyParamName: string = 'companyId';
  readonly ProductParamName: string = 'product';
  readonly RendererTypeParamName: string = 'renderer';

  // Date selection
  selectedMonthDate: Date = new Date();
  parentFromDate: Date = new Date();
  parentToDate: Date = new Date();
  minDate = new Date(2023, 10, 1); // Nov 1, 2023
  maxDate = new Date();

  // Raw data
  rawData: RecipeEventSummary[] = [];
  /** Matching summaries on the server; more than rawData when the month exceeds the page size. */
  totalAvailable = 0;

  // Metrics
  totalRenders = 0;
  completedRenders = 0;
  failedRenders = 0;
  queuedRenders = 0;
  inProgressRenders = 0;
  averageRenderTimeSeconds = 0;
  successRate = 0;

  // Filters
  /**
   * The "All" option's value in the selects. mat-select treats a null or undefined option as a
   * reset and shows nothing, so "All" needs a real value; the filters themselves stay undefined.
   */
  readonly ALL = 'all';
  selectedProductFilter: ProductName | undefined;
  productOptions: IProductNameConfiguration[] = [];
  /** Physical includes summaries with no renderer, such as jobs that never started. */
  selectedRendererTypeFilter: RendererType | undefined;
  readonly rendererTypeOptions = [
    { value: RendererType.Physical, label: 'Physical' },
    { value: RendererType.Virtual, label: 'Virtual' },
  ];

  // Chart data
  dailyChartLabels: Label[] = [];
  dailyChartData: ChartDataSets[] = [];
  productChartLabels: Label[] = [];
  productChartData: number[] = [];
  productChartColors: any[] = [];

  // State
  useDarkTheme = false;
  hasUserNavigatedMonth = false;
  private urlCompanyParam: string | undefined;
  private urlMonthParam: string | undefined;
  private urlProductParam: string | undefined;
  private urlRendererTypeParam: string | undefined;
  private originalCompanyId: string | undefined;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private productNameService: ProductNameService,
    private breadcrumbService: BreadcrumbService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private companyService: CompanyService
  ) {
    super(authService, stateService);

    // Store URL parameters
    this.urlCompanyParam = this.route.snapshot.queryParams[this.CompanyParamName];
    this.urlMonthParam = this.route.snapshot.queryParams[this.MonthParamName];
    this.urlProductParam = this.route.snapshot.queryParams[this.ProductParamName];
    this.urlRendererTypeParam = this.route.snapshot.queryParams[this.RendererTypeParamName];

    // Initialize date
    this.initializeMonthlyDate();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.originalCompanyId = this.selectedCompany?.id;
    // Use getAllConfigurations() to include Connect and other products not in the base configurations array
    this.productOptions = this.productNameService.getAllConfigurations().filter(p =>
      [ProductName.Panorama, ProductName.RayTracedDraft, ProductName.RayTraced, ProductName.RayTracedPlus, ProductName.VR, ProductName.Facade, ProductName.DayDreamFacade, ProductName.Plan, ProductName.Render, ProductName.Connect].includes(p.productName)
    ) || [];

    // Process URL parameters
    this.processUrlParameters();

    // Subscribe to theme changes
    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      this.useDarkTheme = state.useDarkTheme;
      if (this.rawData.length > 0) {
        this.buildCharts();
      }
    }));

    // Load data
    if (!this.urlMonthParam && !this.urlCompanyParam) {
      setTimeout(() => this.loadRenderSummaries(), 100);
    }
  }

  protected onSelectedCompanyChange(): void {
    this.loadRenderSummaries();
  }

  // Date initialization
  initializeMonthlyDate(): void {
    const nowUtc = new Date();
    const previousMonthUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(),
      nowUtc.getUTCMonth() - 1,
      1
    ));
    this.selectedMonthDate = previousMonthUtc;

    const maxDateUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(),
      nowUtc.getUTCMonth(),
      nowUtc.getUTCDate() - 1
    ));
    this.maxDate = maxDateUtc;

    this.updateMonthlyDateRange();
  }

  private updateMonthlyDateRange(): void {
    this.parentFromDate = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth(),
      1, 0, 0, 0, 0
    ));
    this.parentToDate = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() + 1,
      0, 23, 59, 59, 999
    ));
  }

  // Month navigation
  setMonthAndYear(normalizedMonthAndYear: Date, datepicker: MatDatepicker<Date>): void {
    this.selectedMonthDate = new Date(Date.UTC(
      normalizedMonthAndYear.getFullYear(),
      normalizedMonthAndYear.getMonth(),
      1, 0, 0, 0, 0
    ));
    this.hasUserNavigatedMonth = true;
    datepicker.close();
    this.updateMonthlyDateRange();
    this.loadRenderSummaries();
  }

  navigateToPreviousMonth(): void {
    if (this.isPreviousMonthDisabled()) return;

    this.selectedMonthDate = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() - 1,
      1
    ));
    this.hasUserNavigatedMonth = true;
    this.updateMonthlyDateRange();
    this.loadRenderSummaries();
  }

  navigateToNextMonth(): void {
    if (this.isNextMonthDisabled()) return;

    this.selectedMonthDate = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() + 1,
      1
    ));
    this.hasUserNavigatedMonth = true;
    this.updateMonthlyDateRange();
    this.loadRenderSummaries();
  }

  isPreviousMonthDisabled(): boolean {
    if (!this.selectedMonthDate || !this.minDate) return true;

    const prevMonth = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() - 1,
      1
    ));
    const minMonth = new Date(Date.UTC(
      this.minDate.getUTCFullYear(),
      this.minDate.getUTCMonth(),
      1
    ));
    return prevMonth < minMonth;
  }

  isNextMonthDisabled(): boolean {
    if (!this.selectedMonthDate || !this.maxDate) return true;

    const nextMonth = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() + 1,
      1
    ));
    const maxMonth = new Date(Date.UTC(
      this.maxDate.getUTCFullYear(),
      this.maxDate.getUTCMonth(),
      1
    ));
    return nextMonth > maxMonth;
  }

  getDisplayMonthYear(): string {
    if (!this.selectedMonthDate) return '';

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return `${monthNames[this.selectedMonthDate.getUTCMonth()]} ${this.selectedMonthDate.getUTCFullYear()}`;
  }

  // Product filter
  onProductFilterChange(event: MatSelectChange): void {
    this.selectedProductFilter = event.value === this.ALL ? undefined : event.value;
    this.loadRenderSummaries();
  }

  onRendererTypeFilterChange(event: MatSelectChange): void {
    this.selectedRendererTypeFilter = event.value === this.ALL ? undefined : event.value;
    this.loadRenderSummaries();
  }

  // Data loading
  /** Identifies the latest load; responses to earlier ones are ignored. */
  private loadTicket = 0;
  /** The selection rawData was loaded for. */
  private loadedSelection: string | undefined;

  private selectionKey(): string {
    return [this.selectedCompany?.id, this.formatMonthForUrl(this.selectedMonthDate), this.selectedProductFilter, this.selectedRendererTypeFilter].join('|');
  }

  /** False while the data on screen belongs to another month, company or filter, so exports would be mislabelled. */
  get dataMatchesSelection(): boolean {
    return this.loadedSelection === this.selectionKey();
  }

  loadRenderSummaries(): void {
    const ticket = ++this.loadTicket;
    const selection = this.selectionKey();
    this.loading = true;

    const request = new ListRecipeEventSummariesRequest({
      companyId: this.selectedCompany?.id,
      dateFrom: this.parentFromDate,
      dateTo: this.parentToDate,
      productName: this.selectedProductFilter,
      rendererType: this.selectedRendererTypeFilter,
      pageNumber: 1,
      pageSize: 10000
    });

    this.situAnalyticsClient.listRecipeEventSummaries(request)
      .then((response: ListRecipeEventSummariesResponse) => {
        if (ticket !== this.loadTicket) return;
        this.loadedSelection = selection;
        if (response.success && response.items) {
          this.rawData = response.items;
          this.totalAvailable = response.totalItems ?? response.items.length;
          this.processData();
        } else {
          this.rawData = [];
          this.totalAvailable = 0;
          this.processData();
        }
      })
      .catch(() => {
        if (ticket !== this.loadTicket) return;
        this.showMessage('Unable to load render summary data. Please check your connection and try again.');
        this.loadedSelection = undefined;
        this.rawData = [];
        this.totalAvailable = 0;
        this.processData();
      })
      .finally(() => {
        if (ticket === this.loadTicket) this.loading = false;
      });
  }

  // Data processing
  private processData(): void {
    // Calculate metrics
    this.totalRenders = this.rawData.length;
    this.completedRenders = this.rawData.filter(r => !!r.dateCompleted).length;
    this.failedRenders = this.rawData.filter(r => !!r.dateFailed && !r.dateCompleted).length;
    this.queuedRenders = this.rawData.filter(r => !!r.dateQueued && !r.dateStarted && !r.dateCompleted && !r.dateFailed).length;
    this.inProgressRenders = this.rawData.filter(r => !!r.dateStarted && !r.dateCompleted && !r.dateFailed).length;

    this.successRate = this.totalRenders > 0
      ? Math.round((this.completedRenders / this.totalRenders) * 100)
      : 0;

    // Calculate average render time (only completed)
    const completedWithTime = this.rawData.filter(r =>
      !!r.dateCompleted && r.totalTimeInSeconds && r.totalTimeInSeconds > 0
    );
    this.averageRenderTimeSeconds = completedWithTime.length > 0
      ? Math.round(completedWithTime.reduce((sum, r) => sum + (r.totalTimeInSeconds || 0), 0) / completedWithTime.length)
      : 0;

    this.buildCharts();
  }

  private buildCharts(): void {
    this.buildDailyChart();
    this.buildProductChart();
  }

  private buildDailyChart(): void {
    // Group by day
    const dailyCounts = new Map<string, { completed: number; failed: number; inProgress: number }>();

    this.rawData.forEach(summary => {
      const dateKey = summary.dateAdded
        ? new Date(summary.dateAdded).toISOString().split('T')[0]
        : null;
      if (!dateKey) return;

      if (!dailyCounts.has(dateKey)) {
        dailyCounts.set(dateKey, { completed: 0, failed: 0, inProgress: 0 });
      }

      const counts = dailyCounts.get(dateKey)!;
      if (summary.dateCompleted) {
        counts.completed++;
      } else if (summary.dateFailed) {
        counts.failed++;
      } else {
        counts.inProgress++;
      }
    });

    // Sort by date
    const sortedDates = Array.from(dailyCounts.keys()).sort();

    this.dailyChartLabels = sortedDates.map(d => {
      const date = new Date(d);
      return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;
    });

    const completedData = sortedDates.map(d => dailyCounts.get(d)!.completed);
    const failedData = sortedDates.map(d => dailyCounts.get(d)!.failed);

    // Colors based on theme
    const completedColor = this.useDarkTheme ? 'hsl(145, 60%, 45%)' : 'hsl(145, 60%, 55%)';
    const failedColor = this.useDarkTheme ? 'hsl(330, 90%, 45%)' : 'hsl(330, 90%, 55%)';

    this.dailyChartData = [
      {
        data: completedData,
        label: 'Completed',
        backgroundColor: completedColor,
        borderColor: completedColor,
        hoverBackgroundColor: completedColor,
        stack: 'a'
      },
      {
        data: failedData,
        label: 'Failed',
        backgroundColor: failedColor,
        borderColor: failedColor,
        hoverBackgroundColor: failedColor,
        stack: 'a'
      }
    ];
  }

  private buildProductChart(): void {
    // Group by product name
    const productCounts = new Map<ProductName, number>();

    this.rawData.forEach(summary => {
      if (summary.productName !== undefined) {
        productCounts.set(
          summary.productName,
          (productCounts.get(summary.productName) || 0) + 1
        );
      }
    });

    // Build chart data
    const sortedProducts = Array.from(productCounts.entries())
      .sort((a, b) => b[1] - a[1]); // Sort by count descending

    this.productChartLabels = sortedProducts.map(([product]) =>
      this.productNameService.getProductNameConfiguration(product as any)?.product || ProductName[product]
    );

    this.productChartData = sortedProducts.map(([, count]) => count);

    // Generate colors for each product
    const hueStep = 360 / Math.max(sortedProducts.length, 1);
    const colors = sortedProducts.map((_, i) => {
      const hue = (210 + i * hueStep) % 360; // Start from blue
      const saturation = this.useDarkTheme ? 70 : 80;
      const lightness = this.useDarkTheme ? 45 : 55;
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    });

    this.productChartColors = [{
      backgroundColor: colors,
      borderColor: colors.map(() => this.useDarkTheme ? '#2d3748' : '#ffffff'),
      borderWidth: 2
    }];
  }

  // Chart options
  public barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        gridLines: { display: true, color: 'rgba(128, 128, 128, 0.2)' },
        stacked: true,
        scaleLabel: { display: true, labelString: 'Number of Renders' },
        ticks: { beginAtZero: true }
      }],
      xAxes: [{
        gridLines: { display: true, color: 'rgba(128, 128, 128, 0.2)' },
        stacked: true
      }]
    },
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: 20
      }
    }
  };

  public pieChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        usePointStyle: true,
        padding: 12
      }
    }
  };

  // Utility methods
  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0s';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  // Share & Export
  copyShareableUrl(): void {
    const shareableUrl = this.generateShareableUrl();

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(shareableUrl).then(() => {
        this.showMessage('Shareable link copied to clipboard!');
      }).catch(() => {
        this.fallbackCopyTextToClipboard(shareableUrl);
      });
    } else {
      this.fallbackCopyTextToClipboard(shareableUrl);
    }
  }

  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showMessage('Shareable link copied to clipboard!');
    } catch {
      this.showMessage('Unable to copy link. Please copy manually.');
    }

    document.body.removeChild(textArea);
  }

  private generateShareableUrl(): string {
    const baseUrl = window.location.origin + this.router.url.split('?')[0];
    const params = new URLSearchParams();

    if (this.selectedCompany?.id) {
      params.set(this.CompanyParamName, this.selectedCompany.id);
    }
    params.set(this.MonthParamName, this.formatMonthForUrl(this.selectedMonthDate));
    if (this.selectedProductFilter !== undefined) {
      params.set(this.ProductParamName, String(this.selectedProductFilter));
    }
    if (this.selectedRendererTypeFilter !== undefined) {
      params.set(this.RendererTypeParamName, String(this.selectedRendererTypeFilter));
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  private formatMonthForUrl(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  /** Exports are labelled with the selected month, so refuse while the data is for another selection. */
  private canExport(): boolean {
    if (this.loading || !this.dataMatchesSelection) {
      this.showMessage('The report is still loading this selection. Try again in a moment.');
      return false;
    }
    return true;
  }

  saveChartAsPNG(): void {
    if (!this.canExport()) return;
    const canvas = document.getElementById('dailyCanvas') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `render_summary_${this.getDisplayMonthYear().replace(' ', '_')}.png`;
      a.click();
    }
  }

  // CSV Export
  exportToCsv(): void {
    if (!this.canExport()) return;
    if (!this.rawData || this.rawData.length === 0) {
      this.showMessage('No data to export.');
      return;
    }

    const csvData = this.generateCsvData();
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const companyName = this.selectedCompany?.code || 'all';
    const fileName = `render_summary_${companyName}_${this.getDisplayMonthYear().replace(' ', '_')}_${timestamp}.csv`;

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private generateCsvData(): string {
    const headers = [
      'ArtifactId',
      'DateAdded',
      'SourceName',
      'DateFailed',
      'TimeToCompleteSeconds',
      'DifferenceFromAverage',
      'Company',
      'Home',
      'ArtifactKey',
      'Product',
      'Renderer',
      'RendererType'
    ];

    let csv = headers.join(',') + '\r\n';

    // Calculate average for completed renders
    const completedRenders = this.rawData.filter(r => r.dateCompleted && r.totalTimeInSeconds);
    const averageSeconds = completedRenders.length > 0
      ? completedRenders.reduce((sum, r) => sum + (r.totalTimeInSeconds || 0), 0) / completedRenders.length
      : 0;

    this.rawData.forEach(item => {
      const timeToComplete = item.totalTimeInSeconds || 0;
      const diff = timeToComplete - averageSeconds;
      const diffFormatted = timeToComplete > 0
        ? (diff >= 0 ? `+${this.formatSecondsReadable(Math.abs(diff))}` : `-${this.formatSecondsReadable(Math.abs(diff))}`)
        : '';

      const row = [
        item.artifactId || '',
        this.formatDateForCsv(item.dateAdded),
        `"${item.sourceName || ''}"`,
        this.formatDateForCsv(item.dateFailed),
        timeToComplete || '',
        `"${diffFormatted}"`,
        `"${item.companyCode || ''}"`,
        `"${item.home || ''}"`,
        `"${item.artifactKey || ''}"`,
        `"${this.productLabel(item.productName)}"`,
        `"${item.rendererName || ''}"`,
        item.rendererType !== undefined ? RendererType[item.rendererType] : ''
      ];

      csv += row.join(',') + '\r\n';
    });

    // Add summary row
    csv += [
      '',
      '"--- SUMMARY ---"',
      `"Average: ${this.formatSecondsReadable(averageSeconds)}"`,
      '',
      Math.round(averageSeconds),
      `"${this.formatSecondsReadable(averageSeconds)}"`,
      '',
      '',
      '',
      '',
      '',
      ''
    ].join(',') + '\r\n';

    return csv;
  }

  private productLabel(productName: ProductName | undefined): string {
    if (productName === undefined) return '';
    return this.productNameService.getProductNameConfiguration(productName as any)?.product || ProductName[productName];
  }

  private formatSecondsReadable(seconds: number): string {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${mins}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    }
  }

  private getStatusLabel(item: RecipeEventSummary): string {
    if (item.dateCompleted) return 'Completed';
    if (item.dateFailed) return 'Failed';
    if (item.dateStarted) return 'In Progress';
    if (item.dateQueued) return 'Queued';
    return 'Pending';
  }

  private formatDateForCsv(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toISOString();
  }

  // URL parameters
  private processUrlParameters(): void {
    const checkAuthReady = (): void => {
      if (this.authService.isLoggedIn()) {
        if (this.urlCompanyParam) {
          this.setCompanyFromUrlParam(this.urlCompanyParam);
        }
        if (this.urlMonthParam) {
          this.setMonthFromUrlParam(this.urlMonthParam);
        }
        if (this.urlProductParam) {
          const productValue = parseInt(this.urlProductParam, 10);
          if (!isNaN(productValue)) {
            this.selectedProductFilter = productValue as ProductName;
          }
        }
        if (this.urlRendererTypeParam) {
          const rendererTypeValue = parseInt(this.urlRendererTypeParam, 10);
          if (rendererTypeValue in RendererType) {
            this.selectedRendererTypeFilter = rendererTypeValue as RendererType;
          }
        }

        // Clear URL params after processing
        setTimeout(() => this.clearUrlParameters(), 500);

        if (!this.urlCompanyParam) {
          this.loadRenderSummaries();
        }
      } else {
        setTimeout(checkAuthReady, 200);
      }
    };

    setTimeout(checkAuthReady, 100);
  }

  private setMonthFromUrlParam(monthParam: string): void {
    try {
      const [year, month] = monthParam.split('-').map(Number);
      if (year && month && month >= 1 && month <= 12) {
        this.selectedMonthDate = new Date(Date.UTC(year, month - 1, 1));
        this.updateMonthlyDateRange();
        return;
      }
    } catch {
      // Fall through to default
    }
    this.initializeMonthlyDate();
  }

  private setCompanyFromUrlParam(companyParam: string): void {
    if (!this.authService.isAuthorizedForCompanyById(companyParam)) {
      this.showMessage('You are not authorized to view data for the requested company.');
      return;
    }

    this.companyService.getCompanyById(companyParam)
      .then((company) => {
        if (company) {
          this.stateService.state = {
            ...this.stateService.state,
            company
          };
          setTimeout(() => this.loadRenderSummaries(), 100);
        } else {
          this.showMessage('The requested company could not be found.');
        }
      })
      .catch(() => {
        this.showMessage('Error loading company data.');
      });
  }

  private clearUrlParameters(): void {
    const urlTree = this.router.createUrlTree([], {
      queryParams: {},
      preserveFragment: false
    });
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}
